import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';

export interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  orders: any[];
  wishlist: string[];
  createdAt: string;
}

interface CustomerAuthContextType {
  customer: Customer | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password?: string) => Promise<void>;
  register: (data: RegisterCustomerData) => Promise<void>;
  requestMagicLink: (email: string) => Promise<void>;
  verifyMagicLink: (email: string, code: string) => Promise<void>;
  logout: () => void;
  updateProfile: (data: Partial<Customer>) => Promise<void>;
  addToWishlist: (productId: string) => Promise<void>;
  removeFromWishlist: (productId: string) => Promise<void>;
  isInWishlist: (productId: string) => boolean;
}

interface RegisterCustomerData {
  name: string;
  email: string;
  phone?: string;
  address?: string;
  storeUrl: string;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider: React.FC<{ children: ReactNode; storeUrl: string }> = ({ 
  children, 
  storeUrl 
}) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, [storeUrl]);

  const initializeAuth = async () => {
    try {
      const token = localStorage.getItem(`customer_token_${storeUrl}`);
      if (token) {
        // Set token in API headers
        api.defaults.headers.common['Customer-Authorization'] = `Bearer ${token}`;
        
        // Verify token and get customer data
        const response = await api.get(`/api/customers/me`, {
          headers: { 'store-url': storeUrl }
        });
        setCustomer(response.data);
      }
    } catch (error) {
      // Token is invalid, remove it
      localStorage.removeItem(`customer_token_${storeUrl}`);
      delete api.defaults.headers.common['Customer-Authorization'];
      console.error('Failed to initialize customer auth:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password?: string) => {
    try {
      const response = await api.post('/api/customers/login', { 
        email, 
        password,
        storeUrl 
      });
      
      const { token, customer } = response.data;
      
      // Store token
      localStorage.setItem(`customer_token_${storeUrl}`, token);
      api.defaults.headers.common['Customer-Authorization'] = `Bearer ${token}`;
      
      setCustomer(customer);
      toast.success('Login successful!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      throw error;
    }
  };

  const register = async (data: RegisterCustomerData) => {
    try {
      const response = await api.post('/api/customers/register', data);
      
      const { token, customer } = response.data;
      
      // Store token
      localStorage.setItem(`customer_token_${storeUrl}`, token);
      api.defaults.headers.common['Customer-Authorization'] = `Bearer ${token}`;
      
      setCustomer(customer);
      toast.success('Registration successful!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      throw error;
    }
  };

  const requestMagicLink = async (email: string) => {
    try {
      await api.post('/api/customers/magic-link', { 
        email, 
        storeUrl 
      });
      toast.success('Verification code sent to your email');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to send verification code';
      toast.error(message);
      throw error;
    }
  };

  const verifyMagicLink = async (email: string, code: string) => {
    try {
      const response = await api.post('/api/customers/verify-magic-link', { 
        email, 
        code,
        storeUrl 
      });
      
      const { token, customer } = response.data;
      
      // Store token
      localStorage.setItem(`customer_token_${storeUrl}`, token);
      api.defaults.headers.common['Customer-Authorization'] = `Bearer ${token}`;
      
      setCustomer(customer);
      toast.success('Login successful!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Invalid or expired code';
      toast.error(message);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem(`customer_token_${storeUrl}`);
    delete api.defaults.headers.common['Customer-Authorization'];
    setCustomer(null);
    toast.success('Logged out successfully');
  };

  const updateProfile = async (data: Partial<Customer>) => {
    try {
      const response = await api.patch('/api/customers/profile', data, {
        headers: { 'store-url': storeUrl }
      });
      
      setCustomer(response.data);
      toast.success('Profile updated successfully');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update profile';
      toast.error(message);
      throw error;
    }
  };

  const addToWishlist = async (productId: string) => {
    if (!customer) {
      toast.error('Please login to add items to wishlist');
      return;
    }

    try {
      const response = await api.post('/api/customers/wishlist', { 
        productId 
      }, {
        headers: { 'store-url': storeUrl }
      });
      
      setCustomer(prev => prev ? { ...prev, wishlist: response.data.wishlist } : null);
      toast.success('Added to wishlist');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to add to wishlist';
      toast.error(message);
      throw error;
    }
  };

  const removeFromWishlist = async (productId: string) => {
    if (!customer) return;

    try {
      const response = await api.delete(`/api/customers/wishlist/${productId}`, {
        headers: { 'store-url': storeUrl }
      });
      
      setCustomer(prev => prev ? { ...prev, wishlist: response.data.wishlist } : null);
      toast.success('Removed from wishlist');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to remove from wishlist';
      toast.error(message);
      throw error;
    }
  };

  const isInWishlist = (productId: string) => {
    return customer?.wishlist?.includes(productId) || false;
  };

  const value = {
    customer,
    isAuthenticated: !!customer,
    isLoading,
    login,
    register,
    requestMagicLink,
    verifyMagicLink,
    logout,
    updateProfile,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};
